import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
} from 'react-native';
import { useFocusEffect } from 'expo-router';
import { showAlert, showConfirm } from '../../src/utils/alert';
import { activitiesApi, goalsApi, visionsApi } from '../../src/api/client';
import { useTheme } from '../../src/theme/ThemeContext';
import { useThemedStyles } from '../../src/theme/useThemedStyles';
import type { Theme } from '../../src/theme/types';
import { getLocalDateStr, formatWeekdayDate } from '../../src/utils/format';
import { getErrorMessage } from '../../src/utils/errors';
import {
  SCORE_THRESHOLD_EXCELLENT,
  SCORE_THRESHOLD_GOOD,
  SCORE_THRESHOLD_AVERAGE,
} from '../../src/constants';
import { computeWeekScores, type ScoreResult } from '../../src/utils/timescore';
import { isAvailable as llmIsAvailable, reflect as llmReflect } from '../../src/services/llm';
import type { ReflectResult } from '../../src/services/llm';

function getScoreLabel(score: number | null): string {
  if (score === null || score === undefined) return 'N/A';
  if (score >= SCORE_THRESHOLD_EXCELLENT) return 'Excellent';
  if (score >= SCORE_THRESHOLD_GOOD) return 'Good';
  if (score >= SCORE_THRESHOLD_AVERAGE) return 'Average';
  return 'Needs Work';
}

const createStyles = (theme: Theme) => ({
  container: { flex: 1, backgroundColor: theme.colors.bgSecondary },
  contentContainer: { padding: 16, paddingBottom: 32 },
  centered: { flex: 1, justifyContent: 'center' as const, alignItems: 'center' as const, backgroundColor: theme.colors.bgSecondary, paddingHorizontal: 24 },
  loadingText: { marginTop: 12, fontSize: 15, color: theme.colors.textMuted },
  errorIcon: { fontSize: 48, marginBottom: 12, color: theme.colors.warning },
  errorText: { fontSize: 15, color: theme.colors.error, textAlign: 'center' as const, marginBottom: 16 },
  retryButton: { backgroundColor: theme.colors.primary, paddingHorizontal: 24, paddingVertical: 10, borderRadius: 8 },
  retryText: { color: theme.colors.background, fontWeight: '600' as const, fontSize: 15 },
  todayCard: { backgroundColor: theme.colors.background, borderRadius: 16, padding: 24, marginBottom: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06, shadowRadius: 8, elevation: 3, alignItems: 'center' as const },
  todayHeader: { flexDirection: 'row' as const, alignItems: 'center' as const, justifyContent: 'space-between' as const, width: '100%' as const, marginBottom: 12 },
  todayLabel: { fontSize: 14, fontWeight: '600' as const, color: theme.colors.textMuted, textTransform: 'uppercase' as const, letterSpacing: 1 },
  offDayButton: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6, backgroundColor: theme.colors.bgTertiary },
  offDayButtonText: { fontSize: 12, fontWeight: '600' as const, color: theme.colors.textMuted },
  yesterdayScore: { fontSize: 64, fontWeight: '800' as const, lineHeight: 72 },
  todayBadge: { fontSize: 16, fontWeight: '600' as const, marginTop: 4, marginBottom: 8 },
  insightText: { fontSize: 14, color: theme.colors.textSecondary, textAlign: 'center' as const, marginBottom: 12, lineHeight: 20 },
  dayTypeBadge: { backgroundColor: theme.colors.warningBg, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 6, marginBottom: 12 },
  dayTypeBadgeText: { fontSize: 12, fontWeight: '600' as const, color: theme.colors.warningText, textTransform: 'capitalize' as const },
  breakdownToggle: { marginBottom: 12 },
  breakdownToggleText: { fontSize: 13, fontWeight: '600' as const, color: theme.colors.primary },
  breakdown: { width: '100%' as const, gap: 12 },
  howComputedToggle: { width: '100%' as const, marginTop: 16, paddingTop: 12, borderTopWidth: 1, borderTopColor: theme.colors.border },
  howComputedToggleRow: { flexDirection: 'row' as const, alignItems: 'center' as const, justifyContent: 'space-between' as const },
  howComputedToggleText: { fontSize: 13, fontWeight: '600' as const, color: theme.colors.textMuted },
  howComputedToggleArrow: { fontSize: 12, color: theme.colors.textMuted },
  howComputedContent: { marginTop: 12 },
  howComputedIntro: { fontSize: 13, color: theme.colors.textSecondary, lineHeight: 19, marginBottom: 12 },
  howComputedComponentTitle: { fontSize: 13, fontWeight: '600' as const, color: theme.colors.textPrimary, marginTop: 8, marginBottom: 2 },
  howComputedComponentBody: { fontSize: 13, color: theme.colors.textMuted, lineHeight: 19 },
  scoreBarContainer: { width: '100%' as const },
  scoreBarLabel: { flexDirection: 'row' as const, justifyContent: 'space-between' as const, marginBottom: 4 },
  scoreBarLabelText: { fontSize: 13, fontWeight: '500' as const, color: theme.colors.textSecondary },
  scoreBarValue: { fontSize: 13, fontWeight: '700' as const },
  scoreBarTrack: { height: 8, backgroundColor: theme.colors.bgTertiary, borderRadius: 4, overflow: 'hidden' as const },
  scoreBarFill: { height: '100%' as const, borderRadius: 4 },
  scoreBarDescription: { fontSize: 12, color: theme.colors.textMuted, marginTop: 2, lineHeight: 16 },
  noScoreContainer: { paddingVertical: 20, alignItems: 'center' as const },
  noScoreText: { fontSize: 16, fontWeight: '600' as const, color: theme.colors.textSecondary, marginBottom: 6 },
  sparklineCard: { backgroundColor: theme.colors.background, borderRadius: 12, padding: 16, marginBottom: 16, ...theme.shadows.sm },
  sparklineTitle: { fontSize: 14, fontWeight: '700' as const, color: theme.colors.textSecondary, marginBottom: 12 },
  sparkline: { flexDirection: 'row' as const, alignItems: 'flex-end' as const, justifyContent: 'space-around' as const, height: 36, gap: 4 },
  sparklineBar: { flex: 1, borderRadius: 3, maxWidth: 36 },
  sparklineLabels: { flexDirection: 'row' as const, justifyContent: 'space-around' as const, marginTop: 6 },
  sparklineDayLabel: { fontSize: 11, color: theme.colors.textDisabled, fontWeight: '500' as const, flex: 1, textAlign: 'center' as const },
  reflectionsCard: { backgroundColor: theme.colors.background, borderRadius: 12, padding: 16, marginBottom: 16, ...theme.shadows.sm },
  reflectionsTitle: { fontSize: 12, fontWeight: '700' as const, color: theme.colors.textMuted, letterSpacing: 0.5, marginBottom: 12 },
  reflectionLabel: { fontSize: 14, fontWeight: '600' as const, color: theme.colors.textSecondary, marginBottom: 6 },
  reflectionText: { fontSize: 13, color: theme.colors.textMuted, lineHeight: 19 },
  reflectionPlaceholder: { fontSize: 13, color: theme.colors.textDisabled, fontStyle: 'italic' as const, lineHeight: 19 },
  reflectionHighlight: { fontSize: 13, color: theme.colors.textSecondary, lineHeight: 19, marginLeft: 8 },
  emptyHistory: { backgroundColor: theme.colors.background, borderRadius: 12, padding: 24, alignItems: 'center' as const },
  emptyHistoryText: { fontSize: 14, color: theme.colors.textDisabled, textAlign: 'center' as const, lineHeight: 20 },
});

function ScoreBarInner({ label, value, maxValue, description, styles: s, getScoreColor }: { label: string; value: number; maxValue: number; description: string; styles: any; getScoreColor: (score: number | null) => string }) {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
  // getScoreColor's thresholds are for a 0–100 total, so a component's raw
  // points (out of 40/25/20/15) must be scaled before it is coloured.
  const color = getScoreColor(percentage);

  return (
    <View style={s.scoreBarContainer}>
      <View style={s.scoreBarLabel}>
        <Text style={s.scoreBarLabelText}>{label}</Text>
        <Text style={[s.scoreBarValue, { color }]}>
          {`${value} / ${maxValue}`}
        </Text>
      </View>
      <View style={s.scoreBarTrack}>
        <View
          style={[
            s.scoreBarFill,
            { width: `${Math.min(percentage, 100)}%`, backgroundColor: color },
          ]}
        />
      </View>
      {description ? (
        <Text style={s.scoreBarDescription} accessible={false}>{description}</Text>
      ) : null}
    </View>
  );
}

function MiniSparkline({ scores, styles: s, getScoreColor }: { scores: ScoreResult[]; styles: any; getScoreColor: (score: number | null) => string }) {
  if (scores.length === 0) return null;

  return (
    <View style={s.sparkline}>
      {scores.map((sc, i) => {
        const height = Math.max((sc.total / 100) * 32, 4);
        return (
          <View
            key={sc.date}
            style={[
              s.sparklineBar,
              { height, backgroundColor: getScoreColor(sc.total) },
            ]}
          />
        );
      })}
    </View>
  );
}

/** Build a summary string for the LLM reflection prompt. */
function buildScoreSummary(score: ScoreResult): string {
  const b = score.breakdown;
  return [
    `Date: ${score.date}`,
    `Total Score: ${score.total}/100 (${score.dayType})`,
    `Execution: ${score.execution}/40 — ${b.execution.description}`,
    `Alignment: ${score.alignment}/25 — ${b.alignment.description}`,
    `Balance: ${score.balance}/20 — ${b.balance.description}`,
    `Consistency: ${score.consistency}/15 — ${b.consistency.description}`,
  ].join('\n');
}

export default function TimeScoreScreen() {
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();
  const [weekScores, setWeekScores] = useState<ScoreResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showBreakdown, setShowBreakdown] = useState(true);
  const [showHowComputed, setShowHowComputed] = useState(false);
  const [reflection, setReflection] = useState<ReflectResult | null>(null);
  const [reflectionLoading, setReflectionLoading] = useState(false);

  const getScoreColor = useCallback((score: number | null): string => {
    if (score === null || score === undefined) return colors.textDisabled;
    if (score >= SCORE_THRESHOLD_EXCELLENT) return colors.excellent;
    if (score >= SCORE_THRESHOLD_GOOD) return colors.good;
    if (score >= SCORE_THRESHOLD_AVERAGE) return colors.average;
    return colors.poor;
  }, [colors]);

  const computeScores = useCallback(async (showRefresh = false) => {
    if (showRefresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    try {
      // Fetch all data needed for score computation
      const [activitiesRes, goalsRes, visionsRes] = await Promise.all([
        activitiesApi.list(),
        goalsApi.list(),
        visionsApi.list(),
      ]);

      const today = getLocalDateStr();
      const scores = computeWeekScores(today, activitiesRes.data, goalsRes.data, visionsRes.data);
      setWeekScores(scores);

      // Trigger LLM reflection for yesterday's score (non-blocking)
      const yesterdayScore = scores.length >= 2 ? scores[scores.length - 2] : null;
      if (yesterdayScore && yesterdayScore.dayType === 'normal') {
        loadReflection(yesterdayScore);
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to compute scores.'));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  const loadReflection = async (score: ScoreResult) => {
    try {
      const available = await llmIsAvailable();
      if (!available) return;

      setReflectionLoading(true);
      const summary = buildScoreSummary(score);
      const result = await llmReflect(summary);
      if (result.reflection) {
        setReflection(result);
      }
    } catch {
      // LLM unavailable — no reflection shown, that's fine
    } finally {
      setReflectionLoading(false);
    }
  };

  useFocusEffect(
    useCallback(() => {
      computeScores();
    }, [computeScores])
  );

  const handleMarkOffDay = () => {
    showConfirm(
      'Mark as Off Day',
      'This will flag yesterday as an off day (rest, vacation, sick). The score won\'t count against your consistency streak.',
      async () => {
        showAlert('Done', 'Yesterday has been marked as an off day.');
      },
      { confirmText: 'Mark Off Day' }
    );
  };

  // Show yesterday's score as the hero card (today is still in progress)
  const yesterdayScore = weekScores.length >= 2 ? weekScores[weekScores.length - 2] : null;

  if (isLoading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={styles.loadingText}>Computing scores...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.centered}>
        <Text style={styles.errorIcon}>{'\u26A0'}</Text>
        <Text style={styles.errorText}>{error}</Text>
        <TouchableOpacity
          style={styles.retryButton}
          onPress={() => computeScores()}
          accessibilityLabel="Retry"
          accessibilityRole="button"
          testID="time-score.retry-button"
        >
          <Text style={styles.retryText} accessible={false}>Retry</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      refreshControl={
        <RefreshControl
          refreshing={isRefreshing}
          onRefresh={() => computeScores(true)}
          colors={[colors.primary]}
          tintColor={colors.primary}
        />
      }
    >
      {/* Yesterday's Score Card */}
      <View style={styles.todayCard}>
        <View style={styles.todayHeader}>
          <Text style={styles.todayLabel}>Yesterday's Score</Text>
          <TouchableOpacity
            style={styles.offDayButton}
            onPress={handleMarkOffDay}
            accessibilityLabel="Mark as off day"
            accessibilityRole="button"
            testID="time-score.off-day-button"
          >
            <Text style={styles.offDayButtonText} accessible={false}>Off Day</Text>
          </TouchableOpacity>
        </View>

        {yesterdayScore && yesterdayScore.dayType !== 'no_activity' ? (
          <>
            {yesterdayScore.dayType === 'light_day' ? (
              <View style={styles.noScoreContainer}>
                <Text style={styles.noScoreText}>Light day</Text>
                <Text style={styles.insightText}>{yesterdayScore.insight}</Text>
              </View>
            ) : (
              <>
                <Text
                  style={[
                    styles.yesterdayScore,
                    { color: getScoreColor(yesterdayScore.total) },
                  ]}
                >
                  {yesterdayScore.total}
                </Text>
                <Text
                  style={[
                    styles.todayBadge,
                    { color: getScoreColor(yesterdayScore.total) },
                  ]}
                >
                  {getScoreLabel(yesterdayScore.total)}
                </Text>

                <Text style={styles.insightText}>{yesterdayScore.insight}</Text>
              </>
            )}

            {/* Breakdown toggle */}
            <TouchableOpacity
              style={styles.breakdownToggle}
              onPress={() => setShowBreakdown(!showBreakdown)}
              accessibilityLabel={showBreakdown ? 'Hide breakdown' : 'Show breakdown'}
              accessibilityRole="button"
              testID="time-score.breakdown-toggle"
            >
              <Text style={styles.breakdownToggleText} accessible={false}>
                {showBreakdown ? 'Hide' : 'Show'} Breakdown
              </Text>
            </TouchableOpacity>

            {showBreakdown && (
              <>
                <View style={styles.breakdown}>
                  <ScoreBarInner label="Execution" value={yesterdayScore.execution} maxValue={40} description={yesterdayScore.breakdown.execution.description} styles={styles} getScoreColor={getScoreColor} />
                  <ScoreBarInner label="Alignment" value={yesterdayScore.alignment} maxValue={25} description={yesterdayScore.breakdown.alignment.description} styles={styles} getScoreColor={getScoreColor} />
                  <ScoreBarInner label="Balance" value={yesterdayScore.balance} maxValue={20} description={yesterdayScore.breakdown.balance.description} styles={styles} getScoreColor={getScoreColor} />
                  <ScoreBarInner label="Consistency" value={yesterdayScore.consistency} maxValue={15} description={yesterdayScore.breakdown.consistency.description} styles={styles} getScoreColor={getScoreColor} />
                </View>

                {/* How your score is computed */}
                <View style={styles.howComputedToggle}>
                  <TouchableOpacity
                    style={styles.howComputedToggleRow}
                    onPress={() => setShowHowComputed(!showHowComputed)}
                    accessibilityLabel={showHowComputed ? 'Hide score explanation' : 'How your score is computed'}
                    accessibilityRole="button"
                    accessibilityState={{ expanded: showHowComputed }}
                    testID="time-score.how-computed-toggle"
                  >
                    <Text style={styles.howComputedToggleText} accessible={false}>
                      How your score is computed
                    </Text>
                    <Text style={styles.howComputedToggleArrow} accessible={false}>
                      {showHowComputed ? '\u25BE' : '\u25B8'}
                    </Text>
                  </TouchableOpacity>

                  {showHowComputed && (
                    <View style={styles.howComputedContent} testID="time-score.how-computed-content">
                      <Text style={styles.howComputedIntro} accessible={false}>
                        Your daily score (0{'\u2013'}100) measures how well you spent your time across four areas:
                      </Text>

                      <Text style={styles.howComputedComponentTitle} accessible={false}>
                        Execution (up to 40 points)
                      </Text>
                      <Text style={styles.howComputedComponentBody} accessible={false}>
                        How many of your planned activities you completed. Partial credit is generous{'\u2014'}completing half your activities still earns a solid score.
                      </Text>

                      <Text style={styles.howComputedComponentTitle} accessible={false}>
                        Alignment (up to 25 points)
                      </Text>
                      <Text style={styles.howComputedComponentBody} accessible={false}>
                        How well your activities connect to your goals and visions. Activities linked to active goals score highest.
                      </Text>

                      <Text style={styles.howComputedComponentTitle} accessible={false}>
                        Balance (up to 20 points)
                      </Text>
                      <Text style={styles.howComputedComponentBody} accessible={false}>
                        Whether you spread effort across your life areas (visions). Touching 3 or more visions earns full marks.
                      </Text>

                      <Text style={styles.howComputedComponentTitle} accessible={false}>
                        Consistency (up to 15 points)
                      </Text>
                      <Text style={styles.howComputedComponentBody} accessible={false}>
                        How regularly you have been active over the past week, plus whether today was better or worse than your recent average.
                      </Text>
                    </View>
                  )}
                </View>
              </>
            )}
          </>
        ) : (
          <View style={styles.noScoreContainer}>
            <Text style={styles.noScoreText}>
              {yesterdayScore ? 'No activity yesterday.' : 'Complete your first day to see your score.'}
            </Text>
          </View>
        )}
      </View>

      {/* 7-Day Sparkline */}
      {weekScores.length > 1 && (
        <View style={styles.sparklineCard}>
          <Text style={styles.sparklineTitle}>Last 7 Days</Text>
          <MiniSparkline scores={weekScores} styles={styles} getScoreColor={getScoreColor} />
          <View style={styles.sparklineLabels}>
            {weekScores.map((s) => (
              <Text key={s.date} style={styles.sparklineDayLabel}>
                {new Date(s.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'narrow' })}
              </Text>
            ))}
          </View>
        </View>
      )}

      {/* AI Reflection */}
      <View style={styles.reflectionsCard}>
        <Text style={styles.reflectionsTitle}>REFLECTION</Text>
        {reflectionLoading ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <ActivityIndicator size="small" color={colors.primary} />
            <Text style={styles.reflectionPlaceholder}>Generating reflection...</Text>
          </View>
        ) : reflection ? (
          <View>
            <Text style={styles.reflectionText}>{reflection.reflection}</Text>
            {reflection.highlights.length > 0 && (
              <View style={{ marginTop: 8 }}>
                {reflection.highlights.map((h, i) => (
                  <Text key={i} style={styles.reflectionHighlight}>{'\u2022'} {h}</Text>
                ))}
              </View>
            )}
          </View>
        ) : (
          <Text style={styles.reflectionPlaceholder}>
            {yesterdayScore && yesterdayScore.dayType === 'normal'
              ? 'AI reflection unavailable on this device.'
              : 'Complete some activities to see a reflection.'}
          </Text>
        )}
      </View>
    </ScrollView>
  );
}
